import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { initializeApp, deleteApp, getApp } from 'firebase/app';
import {
    User as FirebaseUser,
    getAuth,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup,
    sendPasswordResetEmail,
    sendEmailVerification,
    linkWithCredential,
    linkWithPopup,
    EmailAuthProvider,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { analytics } from '../services/analytics/analytics.service';
import {
    User,
    parseTier,
    SubscriptionTier,
    SubscriptionInfo,
    SubscriptionStatus,
    SubscriptionSource,
} from '../models/index';
import { initialiseTierDefinitions } from '../services/featureFlagService';
import {
    watchSubscription,
    syncRevenueCatStatus,
} from '../services/subscriptionService';
import i18n from '../i18n';

interface AuthContextType {
    currentUser: FirebaseUser | null;
    userProfile: User | null;
    loading: boolean;
    /**
     * True while `users/{uid}` is still being fetched for the CURRENT signed-in
     * user. `loading` only covers the initial auth resolution and is already
     * false by the time a fresh sign-in flips `currentUser`, so anything that
     * reads `userProfile` (route guards, tier checks) must wait on this instead
     * — otherwise it sees a null profile for the few hundred ms before the
     * first snapshot arrives and treats a complete account as incomplete.
     */
    profileLoading: boolean;
    /** Convenience flag — true when the signed-in user has admin privileges. */
    isAdmin: boolean;
    /** Convenience flag — true when the signed-in user's account is blocked. */
    isBlocked: boolean;
    /** Admin-supplied reason text for a suspended account. */
    blockReason: string | undefined;
    /**
     * Live subscription info from `subscriptions/{userId}` (null = no document).
     * Populated after the mandatory RevenueCat sync on login.
     */
    subscriptionInfo: SubscriptionInfo | null;
    /** Convenience accessor — billing lifecycle status from RevenueCat. */
    subscriptionStatus: SubscriptionStatus;
    /** Convenience accessor — which platform originated the entitlement. */
    subscriptionSource: SubscriptionSource;
    /**
     * ISO 3166-1 alpha-2 country code derived from browser locale/timezone.
     * Used by `currencyFormatter` to select the correct currency symbol.
     * Defaults to `'US'` when detection fails.
     */
    userCountry: string;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signInWithApple: () => Promise<void>;
    signOut: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    resendVerificationEmail: (email: string, password: string) => Promise<void>;
    updateUserProfile: (data: Partial<User>) => Promise<void>;
    /** Returns the sign-in methods for an email. Returns [] when enumeration protection hides them. */
    getSignInMethodsForEmail: (email: string) => Promise<string[]>;
    /** Link Google provider to the currently signed-in user via popup. */
    linkGoogleToCurrentUser: () => Promise<void>;
    /** Link Apple provider to the currently signed-in user via popup. */
    linkAppleToCurrentUser: () => Promise<void>;
    /** Link email/password credential to the currently signed-in user. */
    linkEmailPasswordToCurrentUser: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

/**
 * Web email verification shipped on 2026-04-03. Accounts registered before it
 * predate the requirement, so they may still sign in unverified — blocking them
 * would lock out real users over a rule that did not exist when they signed up.
 */
const EMAIL_VERIFICATION_ENFORCED_FROM = new Date('2026-04-04T00:00:00Z');

/** `createdAt` may be a Firestore Timestamp (web) or an ISO string (mobile). */
const parseCreatedAt = (value: unknown): Date | null => {
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string') {
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    }
    const asTimestamp = (value as { toDate?: () => Date } | null)?.toDate;
    if (typeof asTimestamp === 'function') {
        try {
            return (value as { toDate: () => Date }).toDate();
        } catch { /* malformed timestamp — fall through */ }
    }
    return null;
};

/**
 * Whether an unverified user predates the verification requirement.
 *
 * Deliberately NOT "does `users/{uid}` exist?" — `signUp()` writes the profile
 * document at registration, *before* the address is verified, so a bare
 * existence check is true for every new web signup and the gate never fires for
 * anyone. The document's age is the signal that actually separates the legacy
 * accounts the exemption was written for from today's signups.
 *
 * A profile with no readable `createdAt` counts as legacy: mobile's
 * `CompleteUserProfileScreen` never wrote the field, and mobile cannot produce a
 * profile for an unverified email/password account in the first place (see the
 * gate in `lib/main.dart`), so the only documents reaching this branch without a
 * timestamp are genuinely old ones.
 */
const isPreVerificationAccount = (data: Record<string, unknown> | undefined): boolean => {
    const createdAt = parseCreatedAt(data?.createdAt);
    return createdAt === null || createdAt < EMAIL_VERIFICATION_ENFORCED_FROM;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
    const [userProfile, setUserProfile] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [profileLoading, setProfileLoading] = useState(true);
    const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);

    // Detect user's country from browser locale/timezone for currency formatting.
    // Runs once — no external call needed.
    const userCountry = React.useMemo(() => {
        try {
            // 1. Try navigator.language region tag: 'he-IL' → 'IL', 'en-US' → 'US'
            const lang = navigator.language || '';
            const regionFromLang = lang.split('-')[1]?.toUpperCase();
            if (regionFromLang && regionFromLang.length === 2) return regionFromLang;
            // 2. Fallback: timezone heuristic
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (tz.startsWith('Asia/Jerusalem') || tz.startsWith('Asia/Tel_Aviv')) return 'IL';
            if (tz.startsWith('Europe/')) return 'DE'; // generic EU → EUR
        } catch { /* ignore */ }
        return 'US';
    }, []);

    const isAdmin = userProfile?.isAdmin ?? false;
    // Keep admins' own browsing out of the usage counters.
    useEffect(() => { analytics.setExcluded(isAdmin); }, [isAdmin]);
    const isBlocked = userProfile?.isBlocked ?? false;
    const blockReason = userProfile?.blockReason;
    const subscriptionStatus: SubscriptionStatus = subscriptionInfo?.status ?? 'none';
    const subscriptionSource: SubscriptionSource = subscriptionInfo?.source ?? 'none';

    // Parse raw Firestore data into a User object.
    // Supports both snake_case (saved by mobile app) and camelCase (saved by web app).
    const parseUserProfile = (uid: string, data: Record<string, unknown>): User => {
        // birth_date saved as ISO string by mobile; birthDate may be Timestamp from web
        const birthDateRaw = (data.birth_date || data.birthDate) as string | { toDate?: () => Date } | undefined;
        const birthDate = birthDateRaw
            ? (typeof birthDateRaw === 'string' ? new Date(birthDateRaw) : birthDateRaw?.toDate?.())
            : undefined;

        // Tier expiry — stored as ISO string (mobile) or Timestamp (web)
        const tierExpiresRaw = (data.tier_expires_at || data.tierExpiresAt) as string | { toDate?: () => Date } | undefined;
        const tierExpiresAt = tierExpiresRaw
            ? (typeof tierExpiresRaw === 'string'
                ? new Date(tierExpiresRaw)
                : tierExpiresRaw?.toDate?.())
            : undefined;

        const tierAssignedRaw = (data.tier_assigned_at || data.tierAssignedAt) as string | { toDate?: () => Date } | undefined;
        const tierAssignedAt = tierAssignedRaw
            ? (typeof tierAssignedRaw === 'string'
                ? new Date(tierAssignedRaw)
                : tierAssignedRaw?.toDate?.())
            : undefined;

        // Feature overrides: expect an object keyed by feature key
        const rawOverrides = (data.feature_overrides || data.featureOverrides || {}) as Record<string, unknown>;
        const featureOverrides: Record<string, boolean> = {};
        if (typeof rawOverrides === 'object' && rawOverrides !== null) {
            Object.entries(rawOverrides).forEach(([k, v]) => {
                if (typeof v === 'boolean') featureOverrides[k] = v;
            });
        }

        const createdAtRaw = data.createdAt as { toDate?: () => Date } | undefined;
        const updatedAtRaw = data.updatedAt as { toDate?: () => Date } | undefined;

        return {
            id: uid,
            email: (data.email as string) || '',
            realEmail: (data.real_email || data.realEmail) as string | undefined,
            firstName: (data.first_name || data.firstName || '') as string,
            lastName: (data.last_name || data.lastName || '') as string,
            birthDate,
            phoneNumber: (data.phone_number || data.phoneNumber) as string | undefined,
            photoUrl: (data.photo_url || data.photoUrl) as string | undefined,
            baseCurrency: (data.base_currency || data.baseCurrency || 'USD') as string,
            createdAt: createdAtRaw?.toDate?.() || new Date(),
            updatedAt: updatedAtRaw?.toDate?.() || new Date(),
            // Tier
            tier: parseTier(data.tier as string),
            tierAssignedAt,
            tierAssignedBy: (data.tier_assigned_by || data.tierAssignedBy) as string | undefined,
            tierExpiresAt,
            // Admin / blocking
            isAdmin: (data.isAdmin ?? data.is_admin ?? false) as boolean,
            isBlocked: (data.isBlocked ?? false) as boolean,
            blockReason: (data.block_reason || data.blockReason) as string | undefined,
            // Family
            familyGroupId: (data.family_group_id || data.familyGroupId) as string | undefined,
            familyRole: (data.family_role || data.familyRole) as string | undefined,
            featureOverrides,
            // Language preference
            language: (data.language) as string | undefined,
        } as User;
    };

    // Fetch user profile from Firestore (used for one-off reads, e.g. Google sign-in check).
    const fetchUserProfile = async (uid: string): Promise<User | null> => {
        try {
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (userDoc.exists()) {
                return parseUserProfile(uid, userDoc.data() as Record<string, unknown>);
            }
            return null;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            return null;
        }
    };

    // Create user profile in Firestore
    const createUserProfile = async (
        uid: string,
        email: string,
        firstName: string,
        lastName: string
    ): Promise<void> => {
        try {
            await setDoc(doc(db, 'users', uid), {
                email,
                firstName,
                lastName,
                baseCurrency: 'USD',
                // Tier defaults for new users
                tier: SubscriptionTier.Free,
                isAdmin: false,
                is_admin: false,
                isBlocked: false,
                feature_overrides: {},
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error creating user profile:', error);
            throw error;
        }
    };

    // Sign in with email and password
    const signIn = async (email: string, password: string): Promise<void> => {
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            // Reload user to get the freshest emailVerified status from Firebase
            await result.user.reload();
            // Keep authProviders in Firestore current.
            updateAuthProviders().catch(() => { });

            if (!result.user.emailVerified) {
                // Allow users registered before email verification was introduced
                // to log in without verifying — see isPreVerificationAccount for
                // why the test is the profile's age and not its existence.
                let existingDoc;
                try {
                    existingDoc = await getDoc(doc(db, 'users', result.user.uid));
                } catch (firestoreErr) {
                    console.error('[AUTH] Firestore check failed during sign in:', firestoreErr);
                    // Cannot determine if existing user — allow through to avoid lockout
                    analytics.track('login');
                    return;
                }
                if (existingDoc.exists() && isPreVerificationAccount(existingDoc.data())) {
                    // Legacy, already-active user — let them in normally.
                    analytics.track('login');
                    return;
                }

                // New user who hasn't verified yet — send the verification email and block access.
                let emailSent = false;
                try {
                    await sendEmailVerification(result.user);
                    emailSent = true;
                } catch (sendErr: any) {
                    if (sendErr?.code !== 'auth/too-many-requests') {
                        console.warn('[AUTH] sendEmailVerification failed:', sendErr);
                    }
                    emailSent = true;
                }
                await firebaseSignOut(auth);

                const err: any = new Error('Please verify your email before signing in.');
                err.code = 'auth/email-not-verified';
                err.emailSent = emailSent;
                throw err;
            }
            analytics.track('login');
        } catch (error: any) {
            if (error?.code !== 'auth/email-not-verified') {
                console.error('Sign in error:', error);
            }
            throw error;
        }
    };

    // Sign up with email and password
    const signUp = async (
        email: string,
        password: string,
        firstName: string,
        lastName: string
    ): Promise<void> => {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            await createUserProfile(userCredential.user.uid, email, firstName, lastName);
            analytics.track('signup');
            // Send verification email before the user can access the app
            try {
                const actionCodeSettings = { url: `${window.location.origin}/login?verified=true` };
                await sendEmailVerification(userCredential.user, actionCodeSettings);
                console.log('[AUTH] Verification email sent to:', email);
            } catch (emailErr: any) {
                console.error('[AUTH] sendEmailVerification FAILED:', emailErr?.code, emailErr?.message);
            }
            // Sign out so the user must verify their email before accessing the app
            await firebaseSignOut(auth);
        } catch (error) {
            console.error('Sign up error:', error);
            throw error;
        }
    };

    // ── Firestore helpers for collision detection ───────────────────────────

    /**
     * Queries Firestore for a user whose `email` field matches.
     * Returns { uid, authProviders } when found, null otherwise.
     * Used instead of fetchSignInMethodsForEmail because that API always
     * returns [] when Firebase Email Enumeration Protection is enabled.
     */
    const findFirestoreUserByEmail = async (
        email: string
    ): Promise<{ uid: string; authProviders: string[] } | null> => {
        try {
            const q = query(
                collection(db, 'users'),
                where('email', '==', email.trim().toLowerCase())
            );
            const snap = await getDocs(q);
            if (snap.empty) return null;
            const docSnap = snap.docs[0];
            const providers = (docSnap.data().authProviders as string[] | undefined) ?? [];
            return { uid: docSnap.id, authProviders: providers };
        } catch (err) {
            console.warn('[AUTH] Firestore email lookup failed:', err);
            return null;
        }
    };

    /**
     * Persists the current user's provider list to Firestore so that
     * findFirestoreUserByEmail returns accurate provider names on the next
     * collision check. Called fire-and-forget after every successful sign-in.
     */
    const updateAuthProviders = async (): Promise<void> => {
        try {
            const user = auth.currentUser;
            if (!user) return;
            const providers = user.providerData.map((p) => p.providerId);
            // updateDoc, NOT setDoc(merge) — a merge write on a missing document
            // CREATES it, and a brand-new user has no profile yet. That stub makes
            // the app treat them as fully registered and skip profile completion
            // forever, leaving a nameless, email-less, tier-less account. For new
            // users the providers are written by createUserProfile() instead.
            // Mirrors AuthService.updateAuthProviders() on mobile.
            await updateDoc(doc(db, 'users', user.uid), { authProviders: providers });
        } catch (err) {
            // 'not-found' just means the profile does not exist yet — expected.
            console.warn('[AUTH] Could not update authProviders:', err);
        }
    };

    // ────────────────────────────────────────────────────────────────────────

    // Sign in with Google
    const signInWithGoogle = async (): Promise<void> => {
        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);

            // ── Collision detection ──────────────────────────────────────────
            // When Firebase Email Enumeration Protection is on, signInWithPopup
            // silently creates a NEW account (isNewUser=true) instead of
            // throwing account-exists-with-different-credential.
            // Detect this by checking Firestore for an existing user with that email.
            if (result.additionalUserInfo?.isNewUser) {
                const email = result.user.email?.trim().toLowerCase();
                if (email) {
                    const existing = await findFirestoreUserByEmail(email);
                    if (existing) {
                        console.warn('[AUTH] Google: isNewUser=true but Firestore user exists — deleting orphaned account and requesting link.');
                        try { await result.user.delete(); } catch { /* best-effort */ }
                        const enriched: any = new Error('Account exists with different sign-in provider');
                        enriched.code = 'auth/account-exists-with-different-credential';
                        enriched.email = email;
                        enriched.existingProviders = existing.authProviders;
                        throw enriched;
                    }
                }
            }
            // ────────────────────────────────────────────────────────────────

            // Check if user profile exists, if not create one
            const profile = await fetchUserProfile(result.user.uid);
            if (!profile) {
                const displayName = result.user.displayName || '';
                const [firstName, ...lastNameParts] = displayName.split(' ');
                const lastName = lastNameParts.join(' ');
                await createUserProfile(
                    result.user.uid,
                    result.user.email!,
                    firstName,
                    lastName
                );
            }
            // Keep authProviders in Firestore current for future collision checks.
            updateAuthProviders().catch(() => { });
            analytics.track(profile ? 'login' : 'signup');
        } catch (error: any) {
            // Also handle the (rare) case where Firebase does throw the error.
            if (error?.code === 'auth/account-exists-with-different-credential' && !error?.existingProviders) {
                const email = (error?.customData?.email ?? error?.email) as string | undefined;
                if (email) {
                    const existing = await findFirestoreUserByEmail(email.trim().toLowerCase());
                    const enriched: any = new Error('Account exists with different sign-in provider');
                    enriched.code = 'auth/account-exists-with-different-credential';
                    enriched.email = email;
                    enriched.existingProviders = existing?.authProviders ?? [];
                    throw enriched;
                }
            }
            console.error('Google sign in error:', error);
            throw error;
        }
    };

    // Sign in with Apple
    const signInWithApple = async (): Promise<void> => {
        try {
            const provider = new OAuthProvider('apple.com');
            provider.addScope('email');
            provider.addScope('name');
            const result = await signInWithPopup(auth, provider);

            // ── Collision detection (same logic as signInWithGoogle) ──────────
            if (result.additionalUserInfo?.isNewUser) {
                const email = result.user.email?.trim().toLowerCase();
                if (email) {
                    const existing = await findFirestoreUserByEmail(email);
                    if (existing) {
                        console.warn('[AUTH] Apple: isNewUser=true but Firestore user exists — deleting orphaned account and requesting link.');
                        try { await result.user.delete(); } catch { /* best-effort */ }
                        const enriched: any = new Error('Account exists with different sign-in provider');
                        enriched.code = 'auth/account-exists-with-different-credential';
                        enriched.email = email;
                        enriched.existingProviders = existing.authProviders;
                        throw enriched;
                    }
                }
            }
            // ────────────────────────────────────────────────────────────────

            const profile = await fetchUserProfile(result.user.uid);
            if (!profile) {
                const displayName = result.user.displayName || '';
                const [firstName, ...lastNameParts] = displayName.split(' ');
                const lastName = lastNameParts.join(' ');
                await createUserProfile(
                    result.user.uid,
                    result.user.email!,
                    firstName,
                    lastName
                );
            }
            updateAuthProviders().catch(() => { });
            analytics.track(profile ? 'login' : 'signup');
        } catch (error: any) {
            if (error?.code === 'auth/account-exists-with-different-credential' && !error?.existingProviders) {
                const email = (error?.customData?.email ?? error?.email) as string | undefined;
                if (email) {
                    const existing = await findFirestoreUserByEmail(email.trim().toLowerCase());
                    const enriched: any = new Error('Account exists with different sign-in provider');
                    enriched.code = 'auth/account-exists-with-different-credential';
                    enriched.email = email;
                    enriched.existingProviders = existing?.authProviders ?? [];
                    throw enriched;
                }
            }
            console.error('Apple sign in error:', error);
            throw error;
        }
    };

    // ── Account linking helpers ──────────────────────────────────────────────

    const getSignInMethodsForEmail = async (email: string): Promise<string[]> => {
        const existing = await findFirestoreUserByEmail(email);
        return existing?.authProviders ?? [];
    };

    /** Link a Google provider to the currently signed-in user via a popup. */
    const linkGoogleToCurrentUser = async (): Promise<void> => {
        const user = auth.currentUser;
        if (!user) throw new Error('No user is currently signed in');
        const provider = new GoogleAuthProvider();
        await linkWithPopup(user, provider);
    };

    /** Link an Apple provider to the currently signed-in user via a popup. */
    const linkAppleToCurrentUser = async (): Promise<void> => {
        const user = auth.currentUser;
        if (!user) throw new Error('No user is currently signed in');
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        await linkWithPopup(user, provider);
    };

    /** Link an email/password credential to the currently signed-in user. */
    const linkEmailPasswordToCurrentUser = async (password: string): Promise<void> => {
        const user = auth.currentUser;
        if (!user || !user.email) throw new Error('No user is signed in or account has no email');
        const credential = EmailAuthProvider.credential(user.email, password);
        await linkWithCredential(user, credential);
    };

    // Sign out
    const signOut = async (): Promise<void> => {
        try {
            await firebaseSignOut(auth);
            setUserProfile(null);
        } catch (error) {
            console.error('Sign out error:', error);
            throw error;
        }
    };

    // Reset password
    const resetPassword = async (email: string): Promise<void> => {
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (error) {
            console.error('Password reset error:', error);
            throw error;
        }
    };

    // Resend email verification.
    // Uses a SECONDARY Firebase app instance so that the main app's auth state
    // (and onAuthStateChanged) is never disturbed — preventing cross-tab sign-outs.
    const resendVerificationEmail = async (email: string, password: string): Promise<void> => {
        const mainApp = getApp();
        const tempApp = initializeApp(mainApp.options, `temp-resend-${Date.now()}`);
        const tempAuth = getAuth(tempApp);
        try {
            const result = await signInWithEmailAndPassword(tempAuth, email, password);
            const actionCodeSettings = { url: `${window.location.origin}/login?verified=true` };
            await sendEmailVerification(result.user, actionCodeSettings);
        } catch (err: any) {
            if (err?.code === 'auth/too-many-requests') {
                const rateLimitErr: any = new Error(
                    'Verification email already sent. Please wait a few minutes before requesting another, and check your spam/junk folder.'
                );
                rateLimitErr.code = 'auth/verification-rate-limited';
                await deleteApp(tempApp);
                throw rateLimitErr;
            }
            console.error('Resend verification error:', err);
            await deleteApp(tempApp);
            throw err;
        }
        await deleteApp(tempApp);
    };

    // Update user profile
    const updateUserProfile = async (data: Partial<User>): Promise<void> => {
        if (!currentUser) throw new Error('No user logged in');

        try {
            const userRef = doc(db, 'users', currentUser.uid);
            await setDoc(
                userRef,
                {
                    ...data,
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
            // The onSnapshot listener will automatically update userProfile.
        } catch (error) {
            console.error('Error updating user profile:', error);
            throw error;
        }
    };

    // Listen to auth state changes and keep user profile in sync via a real-time
    // Firestore listener. This ensures isBlocked / tier changes made by an admin
    // are reflected immediately without requiring a page reload.
    useEffect(() => {
        let profileUnsubscribe: (() => void) | null = null;

        let subscriptionUnsubscribe: (() => void) | null = null;

        const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);

            // Tear down previous listeners whenever the auth user changes.
            if (profileUnsubscribe) {
                profileUnsubscribe();
                profileUnsubscribe = null;
            }
            if (subscriptionUnsubscribe) {
                subscriptionUnsubscribe();
                subscriptionUnsubscribe = null;
            }

            if (user) {
                // The profile for THIS uid has not been read yet. Stale state
                // from a previous user (or the null of a fresh sign-in) must not
                // be mistaken for "this user has no profile".
                setProfileLoading(true);

                // Pre-load tier definitions so feature flag checks are instant.
                initialiseTierDefinitions().catch(console.error);

                // Real-time listener — fires immediately with current data, then on
                // every subsequent Firestore write (e.g. admin blocks the user).
                profileUnsubscribe = onSnapshot(
                    doc(db, 'users', user.uid),
                    (snapshot) => {
                        // Ignore local pending-write echoes. On login we fire a
                        // fire-and-forget setDoc({authProviders}, {merge:true}); against
                        // the empty in-memory cache Firestore emits a partial local
                        // snapshot containing ONLY that field before the server
                        // round-trip completes. Trusting it would briefly expose a
                        // profile with blank first/last name, which trips PrivateRoute
                        // into redirecting to /profile and makes ProfilePage render
                        // empty. Wait for the server-confirmed snapshot instead.
                        if (snapshot.metadata.hasPendingWrites) return;

                        if (snapshot.exists()) {
                            const profile = parseUserProfile(user.uid, snapshot.data() as Record<string, unknown>);
                            setUserProfile(profile);
                            // Sync language preference from Firestore
                            const lang = (snapshot.data() as Record<string, unknown>).language as string | undefined;
                            if (lang && ['en', 'he'].includes(lang) && i18n.language !== lang) {
                                i18n.changeLanguage(lang).catch(() => { });
                            }
                        } else {
                            setUserProfile(null);
                        }
                        setLoading(false);
                        setProfileLoading(false);
                    },
                    (error) => {
                        console.error('Error listening to user profile:', error);
                        setLoading(false);
                        setProfileLoading(false);
                    }
                );

                // Live subscription listener — keeps subscriptionInfo in sync
                // with Firestore (refreshed by the RC webhook Cloud Function).
                subscriptionUnsubscribe = watchSubscription(
                    user.uid,
                    (info) => setSubscriptionInfo(info),
                    (err) => console.error('Error listening to subscription:', err)
                );

                // Mandatory RC sync on every login — no rate-limit on this path.
                // Corrects any drift between Firestore cache and RevenueCat.
                syncRevenueCatStatus(user.uid).catch((err) =>
                    console.warn('[AuthContext] RC sync failed (non-fatal):', err)
                );
            } else {
                setUserProfile(null);
                setSubscriptionInfo(null);
                setLoading(false);
                setProfileLoading(false);
            }
        });

        return () => {
            authUnsubscribe();
            if (profileUnsubscribe) profileUnsubscribe();
            if (subscriptionUnsubscribe) subscriptionUnsubscribe();
        };

    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const value: AuthContextType = {
        currentUser,
        userProfile,
        loading,
        profileLoading,
        isAdmin,
        isBlocked,
        blockReason,
        subscriptionInfo,
        subscriptionStatus,
        subscriptionSource,
        userCountry,
        signIn,
        signUp,
        signInWithGoogle,
        signInWithApple,
        signOut,
        resetPassword,
        resendVerificationEmail,
        updateUserProfile,
        getSignInMethodsForEmail,
        linkGoogleToCurrentUser,
        linkAppleToCurrentUser,
        linkEmailPasswordToCurrentUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
